import { usePlugins } from '@/contexts/PluginContext'
import { CatalogueFeedModel } from '@/http/models/catalogue-feed'
import { CategoryModel } from '@/http/models/ProductCategory'
import { useQuery } from '@tanstack/react-query'
import { Copy } from 'lucide-react'
import { toast } from 'sonner'

import { useEffect, useState } from 'react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { MultiSelect } from '@/components/ui/MultiSelect'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'

import { PageHeader } from '@/components/atoms'
import { useStorefrontUrl } from '@/hooks/useStorefrontUrl'
import { apiErrorMessage } from '@/lib/apiError'

import { CATALOGUE_FEED_PLUGIN_ID } from './constants'

type FeedConfig = { category_ids?: string[]; include_out_of_stock?: boolean }

const FeedUrl = ({ label, url }: { label: string; url: string }) => (
  <div className="space-y-1.5">
    <Label>{label}</Label>
    <div className="flex items-center gap-2">
      <code className="min-w-0 flex-1 truncate rounded-md bg-subdued px-3 py-1.5 text-xs">{url}</code>
      <Button
        variant="secondary"
        size="icon"
        aria-label={`Copy ${label}`}
        onClick={() => navigator.clipboard.writeText(url).then(() => toast.success('Feed URL copied'), () => toast.error('Could not copy'))}
      >
        <Copy />
      </Button>
    </div>
  </div>
)

/**
 * The catalogue feed plugin: the store's Meta and Google feed URLs, how to add them, and which products go in.
 * The storefront builds the feeds from the live products once an hour.
 */
const CatalogueFeedPage = () => {
  const { getPluginConfig, savePluginConfig } = usePlugins()
  const storefrontUrl = useStorefrontUrl()
  const saved = getPluginConfig<FeedConfig>(CATALOGUE_FEED_PLUGIN_ID)
  const [config, setConfig] = useState<FeedConfig>(saved ?? {})
  const [saving, setSaving] = useState(false)
  useEffect(() => setConfig(saved ?? {}), [saved])

  const categories = useQuery({
    queryKey: ['catalogue-feed-categories'],
    queryFn: async () => (await CategoryModel.getList({ query: { length: 500 } })).data?.result ?? [],
  })

  const status = useQuery({
    queryKey: ['catalogue-feed-status'],
    retry: false,
    queryFn: async () => {
      const res = await CatalogueFeedModel.getStatus()
      if (!res.success || !res.data) throw new Error(apiErrorMessage(res, 'Could not load the feed'))
      return res.data.result
    },
  })

  const save = async () => {
    setSaving(true)
    const ok = await savePluginConfig(CATALOGUE_FEED_PLUGIN_ID, config)
    setSaving(false)
    if (ok) void status.refetch()
    if (ok) toast.success('Catalogue feed settings saved. Feeds update within an hour.')
    else toast.error('Could not save the settings')
  }

  return (
    <div className="space-y-4">
      <PageHeader title="Catalogue feed" description="Your products for Facebook, Instagram and Google Shopping, kept up to date automatically." />

      {storefrontUrl ? (
        <Card>
          <CardHeader>
            <CardTitle>Your feed links</CardTitle>
            <CardDescription>Add a link once: Meta and Google read it again on their own schedule and pick up price, stock and new products.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <FeedUrl label="Facebook and Instagram (Meta)" url={`${storefrontUrl}/feeds/meta.csv`} />
            <FeedUrl label="Google Merchant Center" url={`${storefrontUrl}/feeds/google.xml`} />
          </CardContent>
        </Card>
      ) : (
        <Alert>
          <AlertTitle>Your store has no web address yet</AlertTitle>
          <AlertDescription>Set up your store domain in Settings, then come back for your feed links.</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>What's in your feed</CardTitle>
          <CardDescription>Your products as they are right now. Meta and Google pick up changes the next time they read the feed.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {status.isLoading ? (
            <Skeleton className="h-16" />
          ) : status.isError ? (
            <p className="text-sm text-error">{status.error.message}</p>
          ) : status.data ? (
            <>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-lg bg-subdued p-3">
                  <p className="text-xs text-muted-foreground">Products in the feed</p>
                  <p className="mt-1 text-lg font-semibold tabular-nums text-ink">{status.data.item_count.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">{status.data.in_stock_count.toLocaleString()} in stock</p>
                </div>
                <div className="rounded-lg bg-subdued p-3">
                  <p className="text-xs text-muted-foreground">Out of stock, left out</p>
                  <p className="mt-1 text-lg font-semibold tabular-nums text-ink">{status.data.out_of_stock_left_out.toLocaleString()}</p>
                </div>
                <div className="rounded-lg bg-subdued p-3">
                  <p className="text-xs text-muted-foreground">Need fixing</p>
                  <p className="mt-1 text-lg font-semibold tabular-nums text-ink">{status.data.skipped.length.toLocaleString()}</p>
                </div>
              </div>
              {status.data.skipped.length > 0 && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead>Why it is left out</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {status.data.skipped.map((s) => (
                      <TableRow key={s.id}>
                        <TableCell className="max-w-96 truncate">{s.title || 'Untitled product'}</TableCell>
                        <TableCell>{s.reason}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Which products go in</CardTitle>
          <CardDescription>Products without a name, price or image are always left out, because Meta and Google reject them.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label>Categories</Label>
            <MultiSelect
              options={(categories.data ?? []).map((c: { _id: string; name: string }) => ({ value: c._id, label: c.name }))}
              value={config.category_ids ?? []}
              onChange={(category_ids) => setConfig((c) => ({ ...c, category_ids }))}
              placeholder="All categories"
            />
          </div>
          <div className="flex items-center justify-between gap-4">
            <div>
              <Label htmlFor="feed-out-of-stock">Include out-of-stock products</Label>
              <p className="text-xs text-muted-foreground">They show as out of stock, so ads don't send shoppers to products they can't buy.</p>
            </div>
            <Switch
              id="feed-out-of-stock"
              checked={!!config.include_out_of_stock}
              onCheckedChange={(include_out_of_stock) => setConfig((c) => ({ ...c, include_out_of_stock }))}
            />
          </div>
          <Button onClick={save} disabled={saving}>
            Save
          </Button>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Add it to Facebook and Instagram</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="list-decimal space-y-1.5 pl-5 text-sm text-text-2">
              <li>Open Meta Commerce Manager and create a catalogue (type: E-commerce).</li>
              <li>Go to Catalogue → Data sources → Add items → Data feed.</li>
              <li>Choose "Use a URL", paste the Meta link above and set it to update daily.</li>
              <li>Connect the catalogue to your Facebook pixel in Events Manager, so catalogue ads can follow up on shoppers who viewed products.</li>
            </ol>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Add it to Google</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="list-decimal space-y-1.5 pl-5 text-sm text-text-2">
              <li>Open Google Merchant Center and verify your store's website.</li>
              <li>Go to Products → Add products → Add products from a file.</li>
              <li>Choose "Add a file link", paste the Google link above and set a daily fetch.</li>
              <li>Your products can then appear free in Google Search and Shopping, and in Shopping ads.</li>
            </ol>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default CatalogueFeedPage
